import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  ModalCloseButton,
  Text,
} from "@chakra-ui/react";
import React from "react";

import useGetUIErrorMessage from "../../../../hooks/useGetUIErrorMessage";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  bulkRefundTransactionAsync,
  bulkRefundTransactionReset,
} from "../../../../store/slices/bulkRefundTransactionSlice";
import { PrimaryButton } from "../../CustomButtons";
import { CustomModalFooter } from "../../CustomModal";
import { AnimatedLogo, StatusBox } from "../../Misc";

interface IBulkRefundModal {
  transactionIds: string[];
  close?: () => void;
}

export default function BulkRefundTransactionModal({
  transactionIds,
  close,
}: IBulkRefundModal) {
  const dispatch = useAppDispatch();

  const { status, data } = useAppSelector(
    (state) => state.bulkRefundTransaction,
  );

  const handleBulkRefund = () => {
    dispatch(bulkRefundTransactionAsync(transactionIds));
  };

  const errorMessage = useGetUIErrorMessage({
    message: data?.message,
    errors: data?.errors,
  });

  if (status === "loading") {
    return (
      <Box minH="330px">
        <AbsoluteCenter>
          <AnimatedLogo />
        </AbsoluteCenter>
      </Box>
    );
  }

  if (status === "success") {
    return (
      <>
        <Box minH="330px" mb="82px">
          <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
            <StatusBox variant="success" title="Bulk Refund Initiated.">
              {`${transactionIds.length} 
              ${transactionIds.length === 1 ? "transaction" : "transactions"}
              have been queued for refund. Keep an eye on your dashboard for
              updates.`}
            </StatusBox>
          </AbsoluteCenter>
        </Box>
        <CustomModalFooter>
          <PrimaryButton
            as={ModalCloseButton}
            w="full"
            top="initial"
            right="initial"
          >
            Dismiss
          </PrimaryButton>
        </CustomModalFooter>
      </>
    );
  }

  if (status === "failed") {
    return (
      <>
        <Box minH="330px" mb="82px">
          <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
            <StatusBox variant="error" title="Bulk Refund Failed.">
              {errorMessage}
            </StatusBox>
          </AbsoluteCenter>
        </Box>
        <CustomModalFooter>
          <PrimaryButton
            onClick={() => dispatch(bulkRefundTransactionReset())}
            w="full"
          >
            Retry
          </PrimaryButton>
        </CustomModalFooter>
      </>
    );
  }

  return (
    <>
      <Box mb="-20">
        <Text fontSize="1.5rem" fontWeight={500} textAlign="center">
          Bulk Transaction Refund
        </Text>
        <Text
          fontSize="1.125rem"
          color="gray.200"
          textAlign="center"
          marginTop="12px"
          marginBottom="36px"
        >
          Are you sure you want to refund{" "}
          <strong>
            {transactionIds.length}{" "}
            {transactionIds.length === 1 ? "transaction" : "transactions"}
          </strong>
          ?
        </Text>
        <Flex mx="-7" gap={4} justifyContent="space-between">
          <Button
            sx={{
              bg: "red",
              color: "#ffffff",
              borderRadius: "12px",
              width: "214px",
              height: "48px",
              fontWeight: 0,
              fontSize: "1.125rem",
              "&:hover": {
                bg: "red",
              },
            }}
            onClick={handleBulkRefund}
          >
            Yes, Refund All
          </Button>
          <Button
            sx={{
              bg: "#EBEDEF",
              borderRadius: "12px",
              width: "214px",
              height: "48px",
              fontWeight: 0,
              fontSize: "1.125rem",
              "&:hover": {
                bg: "#EBEDEF",
              },
            }}
            onClick={close}
          >
            Not now
          </Button>
        </Flex>
      </Box>
    </>
  );
}
